import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import { useTRPC } from '~/services/trpc'
import { SettingsContent, type TitleSegment, useSettingTitle } from '~/templates/settings'

import { AudioConfiguration } from './-components/audio-configuration'
import { ChannelFeatureFlags } from './-components/channel-feature-flags'
import { ChannelInfoForm } from './-components/channel-info-form'
import { DangerZone } from './-components/danger-zone'

export const Route = createFileRoute(
  '/_authenticated/$alias/settings/channels/$channelId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const params = useParams({
    from: '/_authenticated/$alias/settings/channels/$channelId',
  })
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const trpc = useTRPC()
  const { data: channel } = useQuery(
    trpc.contactChannels.fromId.queryOptions(params.channelId),
  )

  const navigateToChannels = useCallback(() => {
    navigate({
      to: '/$alias/settings/channels',
      search: location.search,
    })
  }, [location.search, navigate])

  const titleSegments: TitleSegment[] = useMemo(
    () =>
      [{ label: 'Channels', onClick: navigateToChannels }, channel?.name].filter(
        Boolean,
      ) as TitleSegment[],
    [navigateToChannels, channel?.name],
  )

  useSettingTitle(titleSegments)

  return (
    <SettingsContent className="w-full">
      {!!channel && (
        <>
          <ChannelInfoForm channel={channel} />
          <ChannelFeatureFlags channel={channel} />
          {channel.type === 'phone' && <AudioConfiguration channel={channel} />}
          <DangerZone channel={channel} onDeleted={navigateToChannels} />
        </>
      )}
    </SettingsContent>
  )
}
